import type {
  SignalMetadataFieldType,
  SignalMetadataSchemaField,
} from "@repo/app-types/entities/signal";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import type { SimpleSelectOption } from "@repo/ui/Select/simpleSelect";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { IconPlus, IconTrash, IconX } from "@tabler/icons-react";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";

const fieldTypeOptions: SimpleSelectOption<SignalMetadataFieldType>[] = [
  { label: "Text", value: "enum" },
  { label: "Number", value: "number" },
];

export interface MetadataFieldRowProps {
  field: SignalMetadataSchemaField;
  onKeyChange: (key: string) => void;
  onTypeChange: (type: SignalMetadataFieldType) => void;
  onOptionChange: (optionIndex: number, value: string) => void;
  onAddOption: () => void;
  onRemoveOption: (optionIndex: number) => void;
  onRemove: () => void;
  /** This field is what a loaded copilot proposal adds or changes. */
  isHighlighted?: boolean;
}

export const MetadataFieldRow: FC<MetadataFieldRowProps> = ({
  field,
  onKeyChange,
  onTypeChange,
  onOptionChange,
  onAddOption,
  onRemoveOption,
  onRemove,
  isHighlighted = false,
}) => {
  return (
    <div
      className={twMerge(
        "space-y-3 rounded-lg border border-border-light p-3",
        isHighlighted && "border-green-300 bg-green-50",
      )}
    >
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <Input
            label="Label"
            id={`metadata-key-${field.id}`}
            value={field.key}
            onChange={(e) => onKeyChange(e.target.value)}
            placeholder="e.g. competitor_name"
            maxLength={100}
          />
        </div>
        <div className="w-36">
          <Label size={Size.SMALL}>Type</Label>
          <div className="mt-1">
            <SimpleSelect<SignalMetadataFieldType>
              label="Metadata"
              isLabelHidden
              options={fieldTypeOptions}
              value={field.type}
              onChange={onTypeChange}
              placeholder="Select type"
            />
          </div>
        </div>
        <div className="pt-6">
          <IconButton
            variant={ButtonVariant.Secondary}
            icon={<IconTrash className="h-4 w-4" />}
            onClick={onRemove}
          />
        </div>
      </div>

      {field.type === "enum" && (
        <div className="space-y-2">
          <Label size={Size.SMALL}>Options</Label>
          <div className="space-y-2">
            {field.options.map((option, optionIndex) => (
              <div key={optionIndex} className="flex items-center gap-2">
                <div className="flex-1">
                  <Input
                    id={`metadata-option-${field.id}-${optionIndex}`}
                    value={option}
                    onChange={(e) =>
                      onOptionChange(optionIndex, e.target.value)
                    }
                    placeholder={`Option ${optionIndex + 1}`}
                    maxLength={100}
                  />
                </div>
                {field.options.length > 1 && (
                  <IconButton
                    variant={ButtonVariant.Secondary}
                    icon={<IconX className="h-4 w-4" />}
                    onClick={() => onRemoveOption(optionIndex)}
                  />
                )}
              </div>
            ))}
          </div>
          <Button
            variant={ButtonVariant.Secondary}
            icon={<IconPlus className="size-3" />}
            onClick={onAddOption}
          >
            Add option
          </Button>
        </div>
      )}
    </div>
  );
};
